// Where the chat view should go when its content changes.
//
// Extracted from ChatContent's effect so the rule can be stated and tested: it
// was a three-branch ternary chain inside an effect, unreachable to every test in
// the repo.
//
// A NEW MESSAGE MOVES THE VIEW, wherever the reader is — a follow-up is meant to
// be seen. Only updates that carry no new content (thinking indicator, tour
// buttons, a re-render) defer to the reader's own scroll position.
//
// That makes "is this actually new?" the load-bearing question, and it is the
// CALLER's to answer honestly — see ChatContent's first-observation seeding. A
// thread hydrating seconds after boot is not new content arriving; treating it as
// such is what made a quiet chat jump back down while someone was reading it.

/** Distance from the bottom, in px, still counted as "at the bottom". */
export const STICK_THRESHOLD_PX = 80;

/**
 * @param {object} args
 * @param {number} args.scrollTop the container's scrollTop. The chat is
 *   `column-reverse`, so 0 is the visual BOTTOM and scrolling up goes negative —
 *   hence the magnitude, not the sign.
 * @param {boolean} args.isNewMessage the newest item is one that just ARRIVED
 *   (not merely the first one this mount has observed)
 * @param {boolean} args.isNewSummary a forecast assessment just posted
 * @param {boolean} args.newMessageOverflows the new message is taller than the viewport
 * @returns {"summaryTop"|"messageTop"|"bottom"|"stay"} what the caller should do
 */
export function decideChatScroll({ scrollTop, isNewMessage, isNewSummary, newMessageOverflows }) {
  // A newly-posted summary pins its own top: the reader lands on the start of the
  // assessment, not partway down it.
  if (isNewSummary) return "summaryTop";
  // A new message taller than the viewport shows its START, so a long report is
  // read from the top rather than jammed to its last line.
  if (isNewMessage && newMessageOverflows) return "messageTop";
  // A new message follows down. Otherwise the reader's position decides — an
  // update with nothing new in it never moves someone who has scrolled away.
  if (isNewMessage || Math.abs(scrollTop || 0) < STICK_THRESHOLD_PX) return "bottom";
  return "stay";
}

/**
 * Did content ARRIVE, or is this simply the first look at a thread that was
 * already there?
 *
 * The distinction is the whole bug. Both trackers start empty, so the first pass
 * over a non-empty thread compares everything against nothing and calls it new —
 * including an assessment summary from a run that finished long ago, which
 * additionally pins its own top. Threads hydrate a few seconds AFTER boot, so a
 * reader who scrolled up in the meantime was thrown back down by content that had
 * not changed at all.
 *
 * The first observation of a NON-EMPTY thread seeds the trackers and reports
 * nothing new. An empty thread does not seed: there is nothing to mistake for
 * new, and waiting keeps the thread's real first message genuine.
 *
 * @param {object} args
 * @param {boolean} args.seeded whether a non-empty thread has been observed yet
 * @param {string|null} args.newestKey key of the newest item, null when empty
 * @param {string|null} args.prevNewestKey the previous pass's `newestKey`
 * @param {string|null} args.newestSummaryKey key of the newest summary, or null
 * @param {string|null} args.prevSummaryKey the previous pass's `newestSummaryKey`
 * @returns {{isNewMessage: boolean, isNewSummary: boolean, seeded: boolean}}
 */
export function classifyChatChange({
  seeded,
  newestKey,
  prevNewestKey,
  newestSummaryKey,
  prevSummaryKey,
}) {
  const seeding = !seeded && newestKey != null;
  return {
    isNewMessage: !seeding && newestKey !== prevNewestKey,
    isNewSummary: !seeding && !!newestSummaryKey && newestSummaryKey !== prevSummaryKey,
    seeded: seeded || seeding,
  };
}
